import { useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";

import PageLoader from "../../../components/loader/PageLoader";
import PopupProvider from "../../../contexts/popupProvider";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { getLocalValueByKey } from "../../../utils";
import { useCheckSaleAccountMutation } from "../../sales/services";
import { useGetUserPermissionsQuery } from "../services";
import {
    postLogout,
    postSalesLogin,
    selectCurrentUser,
    selectIsAuth,
    selectIsLogin,
    setCurrentUser,
} from "../services/authSlice";
import { ICurrentUser } from "../types/interfaces";
import { getUserRole } from "../utils";

const VerifyLogin = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    // Sales Login
    const salesAccessToken = getLocalValueByKey("sale_accessToken");
    const salesRefreshToken = getLocalValueByKey("sale_refreshToken");
    const hasTokens = !!(salesAccessToken && salesRefreshToken);

    const isLogin = useAppSelector(selectIsLogin);
    const isAuth = useAppSelector(selectIsAuth);
    const currentUser = useAppSelector(selectCurrentUser);

    const [salesUserStatus, { isLoading: checkSalesUserStatusLoading }] = useCheckSaleAccountMutation();
    const { data: usersRole, isError: userPermissionError } = useGetUserPermissionsQuery(undefined, {
        // 當 n8n API 失敗時,使用空陣列作為預設值,避免阻塞整個應用
        pollingInterval: 0,
        refetchOnMountOrArgChange: false,
        refetchOnFocus: false,
        refetchOnReconnect: false,
    });

    // 沒有 token 時，立即將 isAuth 設為 false，讓 PrivateLayout 可以導向 /login
    useEffect(() => {
        if (!hasTokens && isAuth === "loading") {
            dispatch(postLogout());
        }
    }, [hasTokens, isAuth, dispatch]);

    // handle sales login - 有 token 時驗證 session
    useEffect(() => {
        const getSalesSession = async () => {
            try {
                dispatch(postSalesLogin({ accessToken: salesAccessToken, refreshToken: salesRefreshToken }));
                const res = await salesUserStatus().unwrap();
                if (res?.data) {
                    const userInfo: ICurrentUser = {
                        accountId: "salesAccount",
                        email: res.data.email,
                        bindLine: false,
                        username: res.data.display_name,
                        enterpriseAccountId: null,
                        isFirstTimeLogin: false,
                        salesAccountId: res.data.id,
                        role: getUserRole(res?.data?.email, usersRole),
                        org_id: res.data.org_id,
                        enterpriseRoles: null,
                        enterpriseUserId: null,
                    };
                    dispatch(setCurrentUser(userInfo));
                }
            } catch (error) {
                navigate("/login");
                dispatch(postLogout());
            }
        };

        if (hasTokens && !currentUser?.org_id && (usersRole || userPermissionError)) {
            getSalesSession();
        }
    }, [
        isLogin,
        hasTokens,
        currentUser,
        dispatch,
        salesAccessToken,
        salesRefreshToken,
        navigate,
        salesUserStatus,
        usersRole,
        userPermissionError,
    ]);

    if (checkSalesUserStatusLoading) {
        return <PageLoader loading={true} />;
    }
    return (
        <>
            <PopupProvider>
                <Outlet />
            </PopupProvider>
        </>
    );
};
export default VerifyLogin;
